/* globals drupalSettings */

import React, { useGlobal } from 'reactn';

import UserInfo from '../user-info/UserInfo';
import Numpad from '../numpad/numpad';
import { BookingSummary, ErrorSummary } from './Summary';

const ConfirmForm = () => {
    const [existingBooking, setExistingBooking] = useGlobal('existingBooking');
    const [bookingType, setBookingType] = useGlobal('bookingType');
    const [bookingAdults, setBookingAdults] = useGlobal('bookingAdults');
    const [bookingChildren, setBookingChildren] = useGlobal('bookingChildren');
    const [roomNumber, setRoomNumber] = useGlobal('roomNumber');
    const [outsideRestaurant, setOutsideRestaurant] = useGlobal('outsideRestaurant');
    const [packageTypes, setPackageTypes] = useGlobal('packageTypes');
    const [step, setStep] = useGlobal('step');
    const [errorMessage, setErrorMessage] = useGlobal('errorMessage');
    const [errorDescription, setErrorDescription] = useGlobal('errorDescription');
    const [loading, setLoading] = useGlobal('loading');
    const roomNumberDigits = window.drupalSettings.roomNumberDigits;

    const reset = async () => {
        setStep(1);
        setExistingBooking(null);
        setBookingType(null);
        setBookingAdults('');
        setBookingChildren('');
        setErrorMessage(null);
        setErrorDescription(null);
        setRoomNumber('');
        setOutsideRestaurant(false);
    };

    const fetchPackageTypes = async () => {
        const response = await fetch('/api/package-types');

        return await response.json();
    }

    const verifyRoomNumber = async () => {
        setLoading(true);

        const response = await fetch(`/api/package/search?roomNumber=${roomNumber}`);
        const data = await response.json();

        if (response.status === 404) {
            if (packageTypes.length === 0) {
                setPackageTypes(await fetchPackageTypes());
            }

            setErrorMessage(data.error);
            setErrorDescription(Drupal.t("Let's create a new booking."));
            setStep(2);
            setLoading(false);

            return;
        }

        if (!response.ok) {
            setErrorMessage(data.error);
            setStep(3);
            setLoading(false);

            return;
        }

        setExistingBooking(data.booking);
        setStep(2);
        setLoading(false);
    };

    const confirmBooking = async () => {
        setLoading(true);

        try {
            const response = await fetch(`/api/package/${existingBooking.id}/confirm?outsideRestaurant=${+outsideRestaurant}`, { method: 'POST' });

            if (!response.ok) {
                // TODO: Show modal
                throw new Error('Server returned an invalid response.');
            }

            window.location = '/';
        } catch (e) {
            console.error(e.message);
        }

        setLoading(false);
    };

    const submitBooking = async () => {
        setLoading(true);

        try {
            const data = new FormData()
            data.append('packageType', bookingType);
            data.append('adults', bookingAdults);
            data.append('children', bookingChildren);
            data.append('roomNumber', roomNumber);
            data.append('outsideRestaurant', +outsideRestaurant);

            const response = await fetch(`/api/package/submit`, { method: 'POST', body: data });

            if (!response.ok) {
                // TODO: Show modal
                throw new Error('Server returned an invalid response.');
            }

            window.location = '/';
        } catch (e) {
            console.error(e.message);
        }

        setLoading(false);
    };

    return (
        <>
            <div className="l-main-55__left">
                <header className="section header">
                    <a href={drupalSettings.urls.front} className="header__logo">
                        <img src={drupalSettings.logo?.url} alt={drupalSettings.logo?.alt} />
                    </a>
                </header>
                <div className="l-main-55__content">
                    {step === 1 && (
                        <>
                            <h2 className="heading-1">{Drupal.t("What's the room number?")}</h2>
                            <div className="number-input">
                                {[...Array(roomNumberDigits).keys()]
                                    .map(index => (
                                        <span className="number-input__number is-editing" key={index}>{roomNumber[index]}</span>
                                    ))}
                            </div>
                            <div className="buttons">
                                <button
                                    className="button button--normal button--dark-bg"
                                    onClick={verifyRoomNumber}
                                    disabled={loading || roomNumber.length !== roomNumberDigits}
                                >
                                    {Drupal.t('Next')}
                                </button>
                            </div>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <h2 className="heading-1">{Drupal.t('Is the guest taking their breakfast outside the restaurant?')}</h2>
                            <div className="radios">
                                <div className="radio">
                                    <input className="radio__input" type="radio" id="yes" name="outsideRestaurant" defaultChecked={outsideRestaurant} onChange={() => setOutsideRestaurant(!outsideRestaurant)} />
                                    <label className="radio__label" htmlFor="yes">Yes</label>
                                </div>
                                <div className="radio">
                                    <input className="radio__input" type="radio" id="no" name="outsideRestaurant" defaultChecked={!outsideRestaurant} onChange={() => setOutsideRestaurant(!outsideRestaurant)} />
                                    <label className="radio__label" htmlFor="no">No</label>
                                </div>
                            </div>
                            <div className="buttons">
                                <button className="button button--normal button--dark-bg" onClick={reset} disabled={loading}>
                                    {Drupal.t('Back')}
                                </button>
                                <button className="button button--normal button--dark-bg" onClick={() => setStep(existingBooking ? 3 : 4)} disabled={loading}>
                                    {Drupal.t('Next')}
                                </button>
                            </div>
                        </>
                    )}
                    {step === 3 && errorMessage && (
                        <>
                            <div className="number-input">
                                {[...Array(roomNumberDigits).keys()]
                                    .map(index => (
                                        <span className="number-input__number" key={index}>{roomNumber[index]}</span>
                                    ))}
                            </div>
                            <div className="buttons">
                                <button className="button button--normal button--dark-bg" onClick={reset} disabled={loading}>
                                    {Drupal.t('Back')}
                                </button>
                            </div>
                        </>
                    )}
                    {step === 3 && !errorMessage && (
                        <>
                            <h2 className="heading-1">{Drupal.t('Please confirm.')}</h2>
                            <div className="number-input">
                                {[...Array(roomNumberDigits).keys()]
                                    .map(index => (
                                        <span className="number-input__number" key={index}>{roomNumber[index]}</span>
                                    ))}
                            </div>
                            <div className="buttons">
                                <button className="button button--normal button--dark-bg" onClick={reset} disabled={loading}>
                                    {Drupal.t('Back')}
                                </button>
                                <button className="button button--normal button--dark-bg" onClick={confirmBooking} disabled={existingBooking === null || loading}>
                                    {Drupal.t('Finish')}
                                </button>
                            </div>
                        </>
                    )}
                    {step === 4 && (
                        <>
                            <h2 className="heading-1">{Drupal.t('Please select the booking type.')}</h2>
                            <div className="radios">
                                {packageTypes.map(packageType => (
                                    <div className="radio" key={packageType.id}>
                                        <input className="radio__input" type="radio" id={'package-type-' + packageType.id} name="package-type" defaultChecked={bookingType === packageType.id} onChange={() => setBookingType(packageType.id)} />
                                        <label className="radio__label" htmlFor={'package-type-' + packageType.id}>{packageType.code ? `${packageType.description} (${packageType.code})` : packageType.description}</label>
                                    </div>
                                ))}
                            </div>
                            <div className="buttons">
                                <button className="button button--normal button--dark-bg" onClick={reset} disabled={loading}>
                                    {Drupal.t('Back')}
                                </button>
                                <button className="button button--normal button--dark-bg" onClick={() => setStep(5)} disabled={bookingType === null || loading}>
                                    {Drupal.t('Next')}
                                </button>
                            </div>
                        </>
                    )}
                    {step === 5 && (
                        <>
                            <h2 className="heading-1">{Drupal.t('Please enter the amount of adults.')}</h2>
                            <div className="number-input">
                                {[...Array(2).keys()].map(index => (
                                    <span className="number-input__number is-editing" key={index}>{bookingAdults[index]}</span>
                                ))}
                            </div>
                            <div className="buttons">
                                <button className="button button--normal button--dark-bg" onClick={reset} disabled={loading}>
                                    {Drupal.t('Back')}
                                </button>
                                <button className="button button--normal button--dark-bg" onClick={() => setStep(6)} disabled={bookingAdults === '' || loading}>
                                    {Drupal.t('Next')}
                                </button>
                            </div>
                        </>
                    )}
                    {step === 6 && (
                        <>
                            <h2 className="heading-1">{Drupal.t('Please enter the amount of children.')}</h2>
                            <div className="number-input">
                                {[...Array(2).keys()].map(index => (
                                    <span className="number-input__number is-editing" key={index}>{bookingChildren[index]}</span>
                                ))}
                            </div>
                            <div className="buttons">
                                <button className="button button--normal button--dark-bg" onClick={reset} disabled={loading}>
                                    {Drupal.t('Back')}
                                </button>
                                <button className="button button--normal button--dark-bg" onClick={submitBooking} disabled={bookingChildren === '' || loading}>
                                    {Drupal.t('Finish')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="l-main-55__right">
                <UserInfo
                    fullName={drupalSettings.user.fullName}
                    editUrl={drupalSettings.urls.userEdit}
                    logoutUrl={drupalSettings.urls.userLogout}
                />
                <div className="l-main-55__content">
                    {step === 1 && (
                        <Numpad value={roomNumber} setValue={setRoomNumber} maxLength={roomNumberDigits} />
                    )}
                    {step > 1 && step < 5 && errorMessage && (
                        <ErrorSummary message={errorMessage} description={errorDescription} />
                    )}
                    {step === 5 && (
                        <Numpad value={bookingAdults} setValue={setBookingAdults} maxLength={2} />
                    )}
                    {step === 6 && (
                        <Numpad value={bookingChildren} setValue={setBookingChildren} maxLength={2} />
                    )}
                    {existingBooking && <BookingSummary booking={existingBooking} />}
                </div>
            </div>
        </>
    );
};

export default ConfirmForm;
